import { Navigation } from "@/components/ui/navigation";
import { BookOpen } from "lucide-react";
import { groupedSources } from "@/data/sources";

const grouped = groupedSources();

// Friendly display names for categories
const CATEGORY_LABELS: Record<string, string> = {
  "AP SCIENCES": "AP Sciences",
  "AP MATHEMATICS": "AP Mathematics",
  "AP COMPUTER SCIENCE": "AP Computer Science",
  "AP ENGLISH": "AP English",
  "AP HISTORY & SOCIAL STUDIES": "AP History & Social Studies",
  "AP ECONOMICS": "AP Economics",
  "AP PSYCHOLOGY": "AP Psychology",
  "AP ARTS": "AP Arts",
  "AP WORLD LANGUAGES": "AP World Languages",
  "AP HUMAN GEOGRAPHY / SOCIOLOGY / OTHER SOCIAL SCIENCES": "AP Human Geography & Social Sciences",
  "️ MULTI-SUBJECT": "Multi-Subject",
};

export default function Sources() {
  return (
    <div className="min-h-screen bg-soft-gradient text-neutral-900">
      <Navigation />

      <main className="flex flex-col">
        {/* Hero Section */}
        <section className="relative pt-12 pb-8 sm:pt-16 sm:pb-12">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10">
                <BookOpen className="h-8 w-8 text-primary" />
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Sources
            </h1>

            <p className="mt-4 text-base text-neutral-500 max-w-xl mx-auto">
              The sources we used to craft our resources.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="pb-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-6">
            {Object.entries(grouped).map(([category, subjects]) => {
              const label = CATEGORY_LABELS[category] ?? category;
              const subjectEntries = Object.entries(subjects);

              return (
                <div
                  key={category}
                  className="rounded-2xl bg-white/80 border border-neutral-200 p-6 sm:p-8 shadow-sm"
                >
                  <h2 className="text-xl font-extrabold tracking-tight text-neutral-900 mb-5">
                    {label}
                  </h2>

                  <div className="space-y-5">
                    {subjectEntries.map(([subject, sources]) => (
                      <div key={subject || "__root__"}>
                        {subject && (
                          <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-2">
                            {subject}
                          </h3>
                        )}
                        <ul className="space-y-1.5">
                          {sources.map((s) => (
                            <li key={s.url}>
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-neutral-700 hover:text-primary hover:underline underline-offset-2 transition-colors"
                              >
                                {s.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer role="contentinfo" className="mt-10 border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-neutral-500">
            <p>© 2026 Lucen — All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
